import { ERROR_MESSAGES } from './constants';

// ============================================
// Email Validation
// ============================================

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email.trim());
};

// ============================================
// Password Validation
// ============================================

export const isValidPassword = (password: string): boolean => {
  return password.length >= 8;
};

export const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isLongEnough = password.length >= 12;
  
  const score = [hasLower, hasUpper, hasNumber, hasSpecial, isLongEnough].filter(Boolean).length;
  
  if (score >= 4) return 'strong';
  if (score >= 2) return 'medium';
  return 'weak';
};

// ============================================
// Text Sanitization (XSS Prevention)
// ============================================

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
};

export const sanitizeHtml = (text: string): string => {
  return text.replace(/[&<>"'/]/g, (char) => HTML_ENTITIES[char] || char);
};

export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .slice(0, 10000); // Limit length
};

// ============================================
// Form Validation
// ============================================

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export const validateRequired = (value: string, fieldName: string): string | null => {
  if (!value || !value.trim()) {
    return `${fieldName} is required`;
  }
  return null;
};

export const validateTaskForm = (data: {
  title: string;
  description: string;
  assigneeId: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};
  
  if (!data.title.trim()) {
    errors.title = 'Title is required';
  } else if (data.title.length > 200) {
    errors.title = 'Title must be less than 200 characters';
  }
  
  if (data.description.length > 5000) {
    errors.description = 'Description must be less than 5000 characters';
  }
  
  if (!data.assigneeId) {
    errors.assigneeId = 'Please select an assignee';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateProjectForm = (data: {
  name: string;
  description: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};
  
  if (!data.name.trim()) {
    errors.name = 'Project name is required';
  } else if (data.name.length > 100) {
    errors.name = 'Project name must be less than 100 characters';
  }
  
  if (data.description.length > 2000) {
    errors.description = 'Description must be less than 2000 characters';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateUserInvite = (data: {
  name: string;
  email: string;
  role?: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};
  
  if (!data.name.trim()) {
    errors.name = 'Name is required';
  }
  
  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(data.email)) {
    errors.email = ERROR_MESSAGES.VALIDATION.INVALID_EMAIL;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// ============================================
// URL Validation
// ============================================

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
